import type { RefObject } from 'react'

import { useEffect, useRef } from 'react'

interface InViewOptions {
    attribute?: string
    rootMargin?: string
    threshold?: number
    once?: boolean
    onEnter?: () => void
}

/**
 * Sets a data attribute on the element the first time it scrolls into view.
 * The attribute is written straight to the DOM, so the server and the first
 * client render stay identical and React never re-renders for it.
 */
export const useInView = <T extends HTMLElement = HTMLDivElement>({
    attribute = 'data-inview',
    rootMargin = '0px 0px -12% 0px',
    threshold = 0,
    once = true,
    onEnter
}: InViewOptions = {}): RefObject<T | null> => {
    const ref = useRef<T>(null)
    const onEnterRef = useRef(onEnter)

    useEffect(() => {
        onEnterRef.current = onEnter
    }, [onEnter])

    useEffect(() => {
        const node = ref.current
        if (!node) return

        const enter = (): void => {
            node.setAttribute(attribute, '')
            onEnterRef.current?.()
        }

        if (typeof IntersectionObserver === 'undefined') {
            enter()
            return
        }

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    enter()
                    if (once) observer.disconnect()
                }
                else if (!once) {
                    node.removeAttribute(attribute)
                }
            }
        }, { rootMargin, threshold })

        observer.observe(node)

        return () => observer.disconnect()
    }, [attribute, rootMargin, threshold, once])

    return ref
}

/**
 * Same as useInView but waits until the element is well inside the viewport,
 * then sets data-play. Product visuals use it to run their one-time sequence.
 */
export const usePlayOnView = <T extends HTMLElement = HTMLDivElement>(onEnter?: () => void): RefObject<T | null> => {
    return useInView<T>({
        attribute: 'data-play',
        rootMargin: '0px 0px -28% 0px',
        onEnter
    })
}
